import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  auditExportUrl,
  listAudit,
  type AuditEntry,
  type AuditQuery,
  type Me,
} from '../api.js';
import { formatDate } from '../labels.js';
import { VENDOR } from '../brand.js';

/**
 * The audit trail (AD-06), and J1's fourth step: *"Admin opens Audit Viewer and
 * sees the tenant creation, each invite, and each role assignment."*
 *
 * IT READS AS SENTENCES, NOT AS ROWS OF A LOG TABLE
 * -------------------------------------------------
 * `audit_event` stores `action` as `entity.past_tense_verb` — `submittal.approved`,
 * `stage.overridden`. Printed raw that is a developer's vocabulary in front of a
 * quantity surveyor, and the screen it belongs on is the one somebody opens when
 * something has gone wrong and they are trying to work out what happened. So each
 * line is a sentence: **who**, **what**, **which record**, **when**.
 *
 * Unknown actions still render — as the verb with its dot removed rather than a
 * blank. A trail that silently drops an event it does not recognise is worse than
 * an ugly one, and new actions arrive with every module.
 *
 * WHAT THIS SCREEN CANNOT DO, EVER
 * --------------------------------
 * Change anything. There is no edit, no delete and no "hide this". The table has
 * UPDATE and DELETE revoked from the application role, so a control offering it
 * would fail — and the fix somebody would reach for is granting the privilege,
 * which is the exact thing the revoke exists to prevent.
 */

/**
 * Actions that are already a whole sentence, so the entity must NOT be appended.
 *
 * Keyed on the FULL action, not the verb. Without this you get "Demo User signed
 * in a person" and "invited someone to the company an invitation" — the object
 * is in the phrase already, and gluing the entity on the end reads as nonsense
 * on exactly the events a new admin looks at first.
 */
const WHOLE_SENTENCES: Readonly<Record<string, string>> = {
  'auth.login_succeeded': 'signed in',
  'auth.login_failed': 'tried to sign in and failed',
  'auth.logout': 'signed out',
  'user.invited': 'invited someone to the company',
  'user.invitation_accepted': 'joined the company',
  'user.password_reset_issued': 'issued a password reset link',
  'user.password_reset_revoked': 'cancelled a password reset link',
  'user.password_reset_completed': 'set a new password from a reset link',
  // Written by the Pulse, never by a person — the actor column says "System",
  // and the sentence has to agree with it or the two read as a contradiction.
  // `04` §"Permission changes are audited" has required these since day one
  // and had nothing to record until roles became editable (ADR-035).
  'role.permissions_changed': 'changed what a role is allowed to do',
  'role.permissions_reset': 'put a role back to the default permissions',
  'stage.at_risk': 'flagged a stage for attention automatically',
  'stage.risk_cleared': 'cleared a stage’s flag automatically',
  'company.created': 'created the company',
  'company.setup_dismissed': 'put the setup list away',
  'company.setup_restored': 'brought the setup list back',
  'audit.exported': 'exported the audit trail',

  /*
   * THE VENDOR'S OWN ACTS, in the customer's trail.
   *
   * Every one of these is written by somebody from outside the company, and
   * they were all falling through to the generic path — which turns
   * `operator.company_suspended` on a `tenant` into "company suspended the
   * company". These are the entries a customer is most likely to stop and read,
   * so they are the last ones that should read like a machine.
   *
   * The two visits are said in full, including that it was read-only, because
   * the sentence has to stand on its own months later when nobody remembers the
   * phone call it came from.
   */
  'operator.support_visit_started': 'came into your company to look at your work, read only',
  'operator.support_visit_ended': 'left your company',
  'operator.company_suspended': 'suspended your company',
  'operator.company_restored': 'let your company back in',
  'operator.user_unlocked': 'unlocked somebody who was locked out',
  'operator.sessions_revoked': 'signed somebody out of every device',
  'operator.invitation_issued': 'issued a new invitation',
};

/** `entity.verb` → a sentence fragment that takes the entity after it. */
const VERBS: Readonly<Record<string, string>> = {
  created: 'created',
  updated: 'changed',
  deleted: 'deleted',
  removed: 'removed',
  added: 'added',
  revoked: 'cancelled',
  approved: 'approved',
  rejected: 'rejected',
  submitted: 'submitted',
  issued: 'issued',
  overridden: 'overrode',
  reopened: 'reopened',
  started: 'started',
  completed: 'completed',
  rebaselined: 'moved the agreed date of',
  released: 'released',
  voided: 'voided',
};

const NOUNS: Readonly<Record<string, string>> = {
  tenant: 'the company',
  invitation: 'an invitation',
  app_user: 'a person',
  membership: 'a membership',
  project: 'a project',
  project_stage: 'a stage',
  project_membership: 'someone on a project',
  milestone: 'a milestone',
  location: 'a location',
  task: 'a task',
  blocker: 'a blocker',
  comment: 'a comment',
  diary: 'a site diary',
  deliverable: 'a drawing',
  drawing_revision: 'a drawing revision',
  submittal: 'a submittal',
  approval_action: 'an approval',
  material_item: 'a material',
  supplier: 'a supplier',
  snag: 'a snag',
  verification: 'a verification',
  inspection: 'an inspection',
  ncr: 'an NCR',
  budget_line: 'a budget line',
  pulse_override: 'a health override',
  company_setting: 'a company setting',
  audit_event: 'the audit trail',
};

/**
 * Whose name goes at the front of the line.
 *
 * THE OPERATOR CASE IS THE REASON THIS IS A FUNCTION.
 *
 * An operator is deliberately not in `app_user`, so `actor_user_id` is null on
 * everything they do and the join that produces `actor_name` has nothing to
 * find — by design (migration 0039: attributing their act to one of the
 * company's own people would be worse than saying nothing). Their name travels
 * in the payload instead, as `after.by`.
 *
 * Without this, every one of those entries read **"Someone"** — the password
 * reset a stranger issued, the suspension, and now the support visit. That is
 * the single line the customer most needs a name on, and "Someone did something
 * to your company" is the sentence most likely to end a customer relationship.
 *
 * `<VENDOR> support` leads, because who they work for is the fact that
 * explains the entry; the person's name follows it.
 */
function actorOf(entry: AuditEntry): string {
  if (entry.actor_name) return entry.actor_name;
  if (entry.actor_type === 'system') return 'The system';

  if (entry.actor_type === 'operator') {
    const by = (entry.after as { by?: unknown } | null)?.by;
    return typeof by === 'string' && by.trim()
      ? `${VENDOR} support (${by})`
      : `${VENDOR} support`;
  }

  return 'Someone';
}

function describe(entry: AuditEntry): string {
  const whole = WHOLE_SENTENCES[entry.action];
  if (whole) return whole;

  const verb = entry.action.slice(entry.action.indexOf('.') + 1);
  const noun = NOUNS[entry.entity_type] ?? entry.entity_type.replace(/_/g, ' ');
  const said = VERBS[verb];

  // No mapping is not a reason to hide the event. `submittal.resubmit_requested`
  // becomes "requested resubmit — a submittal", which is clumsy and true.
  return said ? `${said} ${noun}` : `${verb.replace(/_/g, ' ')} — ${noun}`;
}

/**
 * `2026-08-12 17:23:31.4+04` → `12 Aug 2026, 17:23`.
 *
 * The TIME is the point. `formatDate` gives the day, and on a trail where six
 * things happened in one afternoon "12 Aug 2026" six times over cannot answer
 * the only question anybody opens this screen with: what happened first.
 */
function formatMoment(iso: string): string {
  const time = /[T ](\d{2}:\d{2})/.exec(iso);
  return time ? `${formatDate(iso)}, ${time[1]}` : formatDate(iso);
}

const ENTITY_CHOICES = [
  ['', 'Anything'],
  ['tenant', 'The company'],
  ['invitation', 'Invitations'],
  ['membership', 'Company memberships'],
  ['project', 'Projects'],
  ['project_stage', 'Stages'],
  ['project_membership', 'Project teams'],
  ['milestone', 'Milestones'],
  ['submittal', 'Submittals'],
  ['material_item', 'Materials'],
  ['ncr', 'NCRs'],
  ['snag', 'Snags'],
  ['budget_line', 'Budget'],
  ['pulse_override', 'Health overrides'],
] as const;

interface Props {
  readonly me: Me;
}

export function AuditTrail({ me }: Props): JSX.Element {
  const [entries, setEntries] = useState<readonly AuditEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [limited, setLimited] = useState(false);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  /** The filters actually in force, as opposed to what is typed in the boxes. */
  const [query, setQuery] = useState<AuditQuery>({});

  const load = useCallback(async (applied: AuditQuery) => {
    setBusy(true);
    try {
      const page = await listAudit(applied);
      setEntries(page.entries);
      setCursor(page.next_cursor);
      setLimited(page.limited_to_own_projects);
      setDenied(false);
      setError(null);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : caught instanceof ApiError ? caught.message : 'Could not read the trail.');
      setEntries([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(query);
  }, [load, query]);

  async function loadMore(): Promise<void> {
    if (!cursor) return;
    setBusy(true);
    try {
      const page = await listAudit(query, cursor);
      setEntries((current) => [...(current ?? []), ...page.entries]);
      setCursor(page.next_cursor);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not read any more.');
    } finally {
      setBusy(false);
    }
  }

  if (denied) {
    return (
      <section>
        <h2>Audit trail</h2>
        <p className="hint">Your role does not include the audit trail.</p>
      </section>
    );
  }

  return (
    <section>
      <div className="section-head">
        <h2>Audit trail</h2>
        {me.can.export_audit && (
          // A real link, not a fetch: the browser downloads it, names the file
          // and shows its own progress, which no amount of JavaScript improves.
          <a className="link-button" href={auditExportUrl(query)} download>
            Export what is shown
          </a>
        )}
      </div>

      <p className="hint">
        Everything that has happened, and who did it. Nothing here can be changed or removed —
        not by anyone, including whoever is reading it.
        {limited && (
          <>
            {' '}
            <strong>You are seeing your own projects only</strong>, which is what your role allows.
          </>
        )}
      </p>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <Filters busy={busy} onApply={setQuery} />

      {entries === null && <p aria-live="polite">Reading the trail…</p>}

      {entries && entries.length === 0 && (
        <div className="empty">
          <p>
            <strong>Nothing matches.</strong>
          </p>
          <p>Widen the dates, or clear the filters.</p>
        </div>
      )}

      {entries && entries.length > 0 && (
        <ul className="audit-list">
          {entries.map((entry) => (
            <li key={entry.id} className="audit-entry">
              <p className="audit-line">
                <strong>{actorOf(entry)}</strong>{' '}
                {describe(entry)}
                {entry.project_code && <> on {entry.project_code}</>}
                <span className="audit-when"> · {formatMoment(entry.occurred_at)}</span>
              </p>

              {entry.reason && <p className="audit-reason">&ldquo;{entry.reason}&rdquo;</p>}

              {(entry.before != null || entry.after != null) && (
                <>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setOpen(open === entry.id ? null : entry.id)}
                  >
                    {open === entry.id ? 'Hide the detail' : 'What changed'}
                    <span className="visually-hidden"> — {entry.action}</span>
                  </button>
                  {open === entry.id && (
                    <Changes before={entry.before} after={entry.after} action={entry.action} />
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {cursor && (
        <div className="button-row">
          <button type="button" className="secondary inline" disabled={busy} onClick={() => void loadMore()}>
            {busy ? 'Reading…' : 'Show older'}
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * Before and after, field by field.
 *
 * A raw JSON dump is the easy thing and the wrong one: the reader wants to know
 * WHICH field moved and to what, and on a snapshot of fifteen keys that is
 * genuinely hard to see. Unchanged keys are dropped.
 *
 * A field the reader is not allowed to see is simply absent from the snapshot —
 * the server removed it (ADR-005), so there is nothing here to hide and nothing
 * to leak.
 */
function Changes({
  before,
  after,
  action,
}: {
  readonly before: unknown;
  readonly after: unknown;
  readonly action: string;
}): JSX.Element {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter(
    (key) => JSON.stringify(b[key]) !== JSON.stringify(a[key]),
  );

  const show = (value: unknown): string =>
    value === undefined || value === null
      ? '—'
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

  if (keys.length === 0) {
    return <p className="hint">Nothing recorded beyond the action itself.</p>;
  }

  return (
    <table className="grid audit-changes">
      <caption className="visually-hidden">What changed in {action}</caption>
      <thead>
        <tr>
          <th scope="col">Field</th>
          <th scope="col">Was</th>
          <th scope="col">Became</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => (
          <tr key={key}>
            <th scope="row">{key.replace(/_/g, ' ')}</th>
            <td data-label="Was">{show(b[key])}</td>
            <td data-label="Became">{show(a[key])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------

function Filters({
  busy,
  onApply,
}: {
  readonly busy: boolean;
  readonly onApply: (query: AuditQuery) => void;
}): JSX.Element {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    onApply({
      ...(entityType ? { entity_type: entityType } : {}),
      ...(action.trim() ? { action: action.trim() } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="aud-entity">What kind of thing</label>
          <select
            id="aud-entity"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            disabled={busy}
          >
            {ENTITY_CHOICES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="aud-action">Action starts with</label>
          <input
            id="aud-action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            disabled={busy}
            placeholder="stage.overridden"
          />
          <p className="hint">A prefix, so &ldquo;stage&rdquo; finds every stage action.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="aud-from">From</label>
          <input
            id="aud-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="aud-to">To</label>
          <input
            id="aud-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={busy}
          />
          <p className="hint">The whole of that day is included.</p>
        </div>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy}>
          {busy ? 'Reading…' : 'Apply'}
        </button>
        <button
          type="button"
          className="secondary inline"
          disabled={busy}
          onClick={() => {
            setEntityType('');
            setAction('');
            setFrom('');
            setTo('');
            onApply({});
          }}
        >
          Clear
        </button>
      </div>
    </form>
  );
}
